/**
 * Pure rules of the Petty Cash pages (no Firestore, unit-tested).
 */
import { PERMISSIONS } from '../../constants/permissions';

/** The dashboard tiles from the custody list (open custodies only; settled ones are counted). */
export const custodyStatsOf = (custodies) => {
    const all = custodies || [];
    const open = all.filter(c => c.status === 'open');
    const settled = all.filter(c => c.status === 'settled' || c.status === 'settlement_submitted');
    const sum = (key) => open.reduce((s, c) => s + (Number(c[key]) || 0), 0);
    return {
        totalOpen: sum('total_amount'),
        totalSpent: sum('spent'),
        totalRemaining: sum('remaining'),
        openCount: open.length,
        settledCount: settled.length
    };
};

/**
 * Which custody actions this user can really complete. Each one writes the
 * collections below, and the database rules (firestore.rules) only let the
 * matching write_* permission through - so a button is shown only when its
 * writes are allowed. Before, every view_custodies user saw them, pressed them,
 * and nothing happened (the failure was ignored; audit ADM-18). This mirrors the
 * rules, it does not change who may do what.
 *   expense add / edit / delete: the expense (write_expenses) + the custody totals (write_custodies)
 *   settlement: the settlement (write_settlements) + the custody status (write_custodies)
 *   reopen / archive: the custody (write_custodies)
 *   delete: the delete_custody page permission + the custody (write_custodies)
 */
export const custodyActionsFor = (hasPermission) => {
    const can = (p) => !!hasPermission(p);
    const custody = can(PERMISSIONS.write_custodies);
    return {
        expenses: custody && can(PERMISSIONS.write_expenses),
        settle: custody && can(PERMISSIONS.write_settlements),
        status: custody,
        remove: custody && can(PERMISSIONS.delete_custody)
    };
};

/** "Could not save: permission denied" style text of a failed `{ success: false, error }`. */
export const custodyErrorText = (res, what) => {
    const err = res && res.error;
    // the store's refusals carry the engine's own words: the custody / expense was changed by someone since it was
    // opened (reload), or the invoice number is already taken
    if (err && (err.code === 'record-changed' || err.code === 'invoice-exists') && err.message) return `${what} failed: ${err.message}`;
    const code = err && (err.code || err.message) ? String(err.code || err.message) : '';
    return `${what} failed${code ? `: ${code}` : ''}.`;
};

const amount = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * The settlement record of a custody (audit ADM-24): the amount spent is settled and the cash still in hand is
 * the difference. It used to be stored as 0 whatever remained, so the assistant always said "difference 0.00".
 */
export const settlementRecord = (custody, day) => ({
    settlement_date: day,
    settled_amount: amount(custody && custody.spent),
    difference: amount(custody && custody.remaining),
});

/**
 * The "Reimbursement" sheet of the custody export: the expenses, then a labelled total row. The total row used to
 * put 4 values under a 3-column header (the custody amount under "Reimbursement", the remaining cash in an
 * unnamed column); now it has its own header: Custody / Spent / Remaining.
 */
export const reimbursementSheetRows = (custody, expenses, dateStr) => [
    [`Total reimbursement for ${dateStr}`],
    ['Description', 'Reimbursement', 'Amount Paid'],
    ...(expenses || []).map((ex) => [ex.description || ex.category, '', Number(ex.amount)]),
    ['', '', ''],
    ['', 'Custody', 'Spent', 'Remaining'],
    ['Total', Number(custody && custody.total_amount), Number(custody && custody.spent), Number(custody && custody.remaining)],
];
